'use client';

import { type ParsedUrlQuery, stringify } from 'querystring';
import type { Map } from 'mapbox-gl';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { SPACE_150, SPACE_450, SPACE_600 } from '@alltrails/denali/tokens';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import { useParkTrailResults } from '@alltrails/explore-service/hooks/useParkTrailResults';
import { updateBoundingBox, updateMapRotation, updateSkipQuery } from '@alltrails/explore-service/redux/exploreServiceSlice';
import type BoundingBox from '@alltrails/explore-service/types/BoundingBox';
import ExploreEventData from '@alltrails/explore-service/types/ExploreEventData';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import getMapRotation from '@alltrails/explore-service/utils/getMapRotation';
import {
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  createDistanceAwayParamString,
  distanceAwayParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import useMap from '@alltrails/maps/hooks/useMap';
import { updateActivePark, updatePoiResults, updateTrailResults } from '@alltrails/maps/redux/reducer';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import type { AreaSearchResult, POISearchResult, TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import useExploreSearchParams, { ExploreParamCategories } from '@/hooks/useExploreSearchParams';
import useMaxPOIPins from '@/hooks/useMaxPOIPins';
import { useNavigateBack } from '@/hooks/useNavigateBack';
import { useResetState } from '@/hooks/useResetState';
import { explorePath } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setInitialMapBounds } from '@/utils/slices/exploreSlice';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

// Distance away is not included here because we kick the user back to Explore Root immediately when distance away is applied,
// so adding it to search params and relying on that value would cause a race condition - instead, the distance away param
// is applied directly to the url when we call router.push instead.
// Similarly, bounding box info is also not included because clicking back from the Park sidebar returns the user to the bounds
// they were at when they entered the Park focus state, so there is no point including bounds in the search params.
const paramsToInclude = ['filters', 'sort'] as ExploreParamCategories[];

type ExploreParkProps = {
  initialBounds: BoundingBox;
  initialPark: AreaSearchResult;
  searchParams: ParsedUrlQuery;
};

const ExplorePark = ({ initialBounds, initialPark, searchParams }: ExploreParkProps) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const maxPOIPins = useMaxPOIPins();
  const router = useRouter();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const experiments = useExperiments();
  const debugEnabled = experiments['mugen-explore-debug']?.value === 'on';
  const { distanceAway, mapPadding, searchResults } = useAppSelector(state => ({
    distanceAway: state.exploreService.distanceAway,
    mapPadding: state.explore.mapPadding,
    searchResults: state.exploreService.searchResults
  }));
  // A truthy `naturalLanguageSearchIsActive` at mount signals an in-app NLS redirect. Direct/deep-link park entries
  // don't hydrate this redux flag, so they fall through to the non-seeded branch (no NLS summary, no seeded results).
  const naturalLanguageSearchIsActive = useAppSelector(state => state.exploreService.naturalLanguageSearchIsActive);
  const { initialFilters, initialSeasonalMonth, initialSort, initialTrailEngagement } = useExploreSearchParams(paramsToInclude);
  const [seededFromNls] = useState(naturalLanguageSearchIsActive);
  useParkTrailResults(initialPark);
  const returnToExploreRoot = useNavigateBack();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const didLoginRef = useRef(false);

  const resetState = useMemo<Parameters<typeof useResetState>[0]>(
    () => ({
      persistKeys: {
        conditions: ['activeCondition', 'preloadedOverlayUTCs', 'selectedUTC'],
        explore: ['hasNavigatedClientSide', 'headerHeight', 'initialMapBounds', 'initialMapCenter', 'mapPadding', 'returningBounds'],
        exploreService: seededFromNls
          ? [
              'boundingBox',
              'mapRotation',
              'naturalLanguageQuery',
              'naturalLanguageSearchAnalyticData',
              'naturalLanguageSearchFeedback',
              'naturalLanguageSearchSummary',
              'searchResults',
              'searchResultsSummary'
            ]
          : ['boundingBox', 'mapRotation']
      },
      initialState: {
        explore: { exploreViewType: 'parks page', showFilterBar: true },
        exploreService: {
          filters: initialFilters,
          seasonalMonth: initialSeasonalMonth,
          // Seeded entries hold skipQuery=true and only release it on a user-initiated moveend so the seeded
          // results survive until the user actively pans. Non-seeded entries also start with skipQuery=true —
          // the programmatic fitBounds moveend releases it, kicking off the park trails query.
          skipQuery: true,
          sort: initialSort,
          trailEngagement: initialTrailEngagement
        },
        maps: { poiResults: {}, activePark: initialPark }
      }
    }),
    // Only reset the store on initial render. `initialPark` prop is a non-memoized object coming from server, so avoid putting it in the dependency array.
    [initialFilters, initialSeasonalMonth, initialSort, initialTrailEngagement, seededFromNls]
  );
  useResetState(resetState);
  const onSignUpClose = useCallback(() => {
    if (!didLoginRef.current) {
      returnToExploreRoot();
    }
  }, [returnToExploreRoot]);

  useEffect(() => {
    if (!searchResults) {
      return;
    }

    const trailResults: TrailSearchResult[] = [];
    const poiResults: POISearchResult[] = [];
    searchResults.forEach(result => {
      if (result.type === 'trail') {
        trailResults.push(result);
      } else if (result.type === 'poi') {
        poiResults.push(result);
      }
    });

    dispatch(updateTrailResults(trailResults));
    dispatch(updatePoiResults(poiResults.slice(0, maxPOIPins)));
  }, [dispatch, maxPOIPins, searchResults]);

  useEffect(() => {
    const eventData: ExploreEventData = { boundingBox: initialBounds, mapRotation: 0 };
    const buffer = SPACE_150; // prevent the park bounding box from touching the edges of the map
    map?.fitBounds(
      [
        [initialBounds.bottomLeft.lng, initialBounds.bottomLeft.lat],
        [initialBounds.topRight.lng, initialBounds.topRight.lat]
      ],
      {
        padding: {
          top: (mapPadding?.top ?? 0) + SPACE_450 + buffer, // padding for the filter buttons
          right: (mapPadding?.right ?? 0) + SPACE_600 + buffer, // padding for the map control buttons
          bottom: (mapPadding?.bottom ?? 0) + buffer, //
          left: (mapPadding?.left ?? 0) + buffer
        }
      },
      eventData
    );

    const atBounds: AllTrailsBounds = {
      latitudeBottomRight: initialBounds.bottomRight.lat,
      longitudeBottomRight: initialBounds.bottomRight.lng,
      latitudeTopLeft: initialBounds.topLeft.lat,
      longitudeTopLeft: initialBounds.topLeft.lng
    };
    dispatch(setInitialMapBounds(atBounds));
    // Only run on initial render. `intialBounds` prop is a non-memoized object coming from server, so avoid putting it in the dependency array.
  }, [dispatch, map, mapPadding]);

  useEffect(() => {
    const onMoveEnd = (event: { target: Map; originalEvent?: unknown } & Partial<ExploreEventData>) => {
      if (debugEnabled) {
        console.log('map zoom: ', map?.getZoom());
      }

      const boundingBox = getMapBoundingBox(event.target);
      const mapRotation = getMapRotation(event.target);
      dispatch(updateBoundingBox(boundingBox));
      dispatch(updateMapRotation(mapRotation));
      // Only keep skipQuery=true if the moveend was not user-initiated or if the page was seeded from an NLS response.
      // This keeps the seeded results alive even after the initial pan animation ends.
      const userInitiated = Boolean(event.originalEvent);
      if (userInitiated || !seededFromNls) {
        dispatch(updateSkipQuery(false));
      }
    };

    map?.on('moveend', onMoveEnd);
    return () => {
      map?.off('moveend', onMoveEnd);
    };
  }, [debugEnabled, dispatch, map, seededFromNls]);

  useEffect(() => {
    const showSignUpModal = () => {
      if (!user) {
        setIsSignUpModalOpen(true);
      }
    };
    map?.once('moveend', showSignUpModal);
  }, [map, user]);

  useEffect(() => {
    if (map && distanceAway) {
      const newSearchParams = new URLSearchParams(stringify(searchParams));
      const boundingBox = getMapBoundingBox(map);
      newSearchParams.append(bottomRightLatParamKey, String(boundingBox.bottomRight.lat));
      newSearchParams.append(bottomRightLngParamKey, String(boundingBox.bottomRight.lng));
      newSearchParams.append(topLeftLatParamKey, String(boundingBox.topLeft.lat));
      newSearchParams.append(topLeftLngParamKey, String(boundingBox.topLeft.lng));
      newSearchParams.append(distanceAwayParamKey, createDistanceAwayParamString(distanceAway));
      router.push(`${wrapUrlSafe(explorePath, languageRegionCode)}?${decodeURIComponent(newSearchParams.toString())}`);
    }
  }, [distanceAway, languageRegionCode, map, router, searchParams]);

  useEffect(() => {
    dispatch(updateActivePark(initialPark));
  }, [dispatch, initialPark]);

  return (
    <>
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={onSignUpClose}
          isOpen
          onSuccess={() => {
            didLoginRef.current = true;
            setIsSignUpModalOpen(false);
          }}
          trigger="park focus state"
          upsellTrigger="park focus state"
        />
      )}
    </>
  );
};

export default ExplorePark;
